import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as Icons from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from "react-i18next";
import { Category, CategoryEntityType } from "@/types/category";
import {
  useCreateCategory,
  useUpdateCategory,
} from "@/hooks/useCategories.hooks";
import { useHotel } from "@/hooks/useHotel";
import { cn } from "@/lib/utils";
import { newCategoryPayload } from "./utils/newCategoryPayload";

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  category?: Category;
  entityType: CategoryEntityType;
}

interface CategoryFormValues {
  name: string;
  icon: string;
  hasIcon: boolean;
}

const ICON_LIST = Object.keys(Icons).filter(
  (key) =>
    typeof (Icons as any)[key] === "function" || (Icons as any)[key]?.render,
);

export const CategoryModal: React.FC<CategoryModalProps> = ({
  isOpen,
  onClose,
  category,
  entityType,
}) => {
  const { t } = useTranslation();
  const { hotelId } = useHotel();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const [iconSearch, setIconSearch] = useState("");

  const { register, handleSubmit, setValue, watch, reset } =
    useForm<CategoryFormValues>({
      defaultValues: {
        name: "",
        icon: "",
        hasIcon: false,
      },
    });

  const hasIcon = watch("hasIcon");
  const selectedIcon = watch("icon");

  useEffect(() => {
    if (category) {
      reset({
        name: category.name,
        icon: category.icon || "",
        hasIcon: !!category.icon,
      });
    } else {
      reset({
        name: "",
        icon: "",
        hasIcon: false,
      });
    }
  }, [category, reset, isOpen]);

  const onSubmit = (values: CategoryFormValues) => {
    if (!hotelId) return;

    const data = {
      name: values.name,
      icon: values.hasIcon ? values.icon : undefined,
      entityType,
      hotelId,
    };

    if (category) {
      updateCategory.mutate(
        { id: category.id, data },
        {
          onSuccess: () => onClose(),
        },
      );
    } else {
      createCategory.mutate(newCategoryPayload(data), {
        onSuccess: () => onClose(),
      });
    }
  };

  const filteredIcons = ICON_LIST.filter((name) =>
    name.toLowerCase().includes(iconSearch.toLowerCase()),
  ).slice(0, 50);

  return (
    <Dialog open={isOpen} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogOverlay />
      <DialogContent
        showCloseButton={false}
        className="border-base-border flex max-h-[min(720px,calc(100vh-48px))] w-[520px] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-xl border bg-white p-0 shadow-lg sm:max-w-[520px]"
      >
        <DialogHeader className="border-base-border relative shrink-0 gap-1 border-b px-6 py-4 pr-14 text-left">
          <DialogTitle className="text-base leading-normal font-semibold text-black/96">
            {category
              ? t("categories:editCategory")
              : t("categories:createCategory")}
          </DialogTitle>
          <DialogDescription className="text-sm leading-normal text-black/65">
            {category
              ? t("categories:editCategoryDescription")
              : t("categories:createCategoryDescription")}
          </DialogDescription>
          <DialogClose asChild>
            <Button
              type="button"
              variant="new_ghost"
              size="icon_sm"
              className="absolute top-3 right-4 rounded-md text-black/55"
              aria-label={t("categories:close")}
            >
              <Icons.X />
            </Button>
          </DialogClose>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
            <Input
              id="name"
              label={t("categories:name")}
              {...register("name", { required: true })}
              placeholder={t("categories:enterName")}
            />

            <div className="flex items-center gap-3">
              <Switch
                id="hasIcon"
                checked={hasIcon}
                onCheckedChange={(checked) => setValue("hasIcon", checked)}
              />
              <Label htmlFor="hasIcon" className="text-black/96">
                {t("categories:useIcon")}
              </Label>
            </div>

            {hasIcon && (
              <div className="flex flex-col gap-3">
                <Label className="text-black/96">
                  {t("categories:selectIcon")}
                </Label>
                <Input
                  icon={<Icons.Search className="size-4 text-black/45" />}
                  placeholder={t("categories:searchIcon")}
                  value={iconSearch}
                  onChange={(e) => setIconSearch(e.target.value)}
                />
                <div className="border-base-border grid max-h-48 grid-cols-6 gap-2 overflow-y-auto rounded-lg border bg-white p-2">
                  {filteredIcons.map((iconName) => {
                    const IconComponent = (Icons as any)[iconName];
                    return (
                      <Button
                        key={iconName}
                        type="button"
                        variant="new_ghost"
                        size="icon_sm"
                        onClick={() => setValue("icon", iconName)}
                        className={cn(
                          "border border-transparent",
                          selectedIcon === iconName &&
                            "border-custom-outline bg-secondary-badge",
                        )}
                        title={iconName}
                      >
                        {IconComponent && <IconComponent />}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-base-border shrink-0 flex-row justify-end gap-2 border-t px-6 py-4">
            <Button type="button" variant="new_outline" onClick={onClose}>
              {t("categories:cancel")}
            </Button>
            <Button
              type="submit"
              variant="new_default"
              disabled={createCategory.isPending || updateCategory.isPending}
            >
              {category ? t("categories:update") : t("categories:create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
